import React, { useEffect, useState } from 'react';
import { BulbOutlined } from '@ant-design/icons';
import css from './resumeCoaching.module.scss';

/* Coaching content for the resume-tailoring flow: WHY we rebuild the user's
 * resume onto a clean, standardized template. Job Beast coaches with evidence,
 * so every number here is traceable to a real source (see the citations) — no
 * viral, un-sourceable stats (e.g. the "75% auto-rejected" myth is deliberately
 * excluded). This SUPPLEMENTS the outreach playbook (jobbeast.org/playbook):
 * the playbook argues you should route around the application; this explains
 * how to make the resume itself survive both the software screen and the human
 * skim. Single source of truth shared by the waiting screen and the editor's
 * "Why this layout?" explainer. */

export const PLAYBOOK_URL = 'https://www.jobbeast.org/playbook';

/** Short, plain coaching lines rotated on the waiting screen. Each is tied to a
 *  verified stat in TEMPLATE_EVIDENCE. Kept encouraging and specific, not
 *  salesy. */
export const WAITING_TIPS: string[] = [
  'Recruiters spend about 7.4 seconds on their first look at a resume. We’re arranging yours so the essentials land in that glance.',
  'Most large employers screen resumes with software before a person reads them. We’re formatting yours to stay readable to the software and the person.',
  'The resumes recruiters rate highest use simple sections and clear headings — not columns and clutter. That’s the layout we’re building.',
  'We also tune the wording to each role: tailored resumes won about 31% more interview callbacks in a large field study.',
];

export interface EvidencePoint {
  point: string;
  /** One-sentence version, for the first-run explainer. That modal has to be
   *  READ by someone who just got a surprise, so it trades completeness for a
   *  glance; `detail` keeps the full argument for whoever goes looking. */
  short: string;
  detail: string;
  source: string;
}

/** The fuller, sourced explanation shown on demand in the editor. */
export const TEMPLATE_EVIDENCE: EvidencePoint[] = [
  {
    point: 'It gets past the software screen',
    short:
      'Screening software garbles columns and graphics. A single clean column stays readable.',
    detail:
      'Most large employers use software to screen resumes before a person sees them. That software can garble resumes built with columns, tables, or graphics — one common system, given a two-column design, dropped the skills, contact details, and links entirely. A single clean column stays machine-readable.',
    source: 'Harvard Business School & Accenture, 2021; Jobscan',
  },
  {
    point: 'It reads well in a 7-second glance',
    short:
      'A recruiter’s first pass averages 7.4 seconds. Simple sections win it.',
    detail:
      'A recruiter’s first pass averages about 7.4 seconds. In eye-tracking research the resumes that scored best used simple sections and clear headings — exactly what this layout gives you — while cluttered, multi-column ones performed worst.',
    source: 'Ladders eye-tracking study, 2018',
  },
  {
    point: 'Tailored wording gets more replies',
    short:
      'Resumes matched to the job earned about 31% more callbacks.',
    detail:
      'In a large field experiment, resumes matched to the specific job earned about 31% more interview callbacks than generic ones — so we also tune your wording to each role.',
    source: 'ResumeGo, 2019',
  },
];

/** Each tip is two full sentences; at ~200 wpm they take 6–8s just to read, so
 *  a shorter dwell cut people off mid-line. 11s reads comfortably with a beat
 *  to absorb before the next. */
const TIP_INTERVAL_MS = 11000;

/**
 * Rotating, evidence-backed coaching tip for the "preparing" waiting screen —
 * turns dead wait time into a teachable moment. Holds on the first tip for
 * reduced-motion users; otherwise advances every ~11s and PAUSES while the
 * pointer rests on it, so a tip is never yanked mid-read.
 */
export const WaitingCoach: React.FC = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (reduceMotion || paused) return;
    const timer = setInterval(
      () => setIndex((n) => (n + 1) % WAITING_TIPS.length),
      TIP_INTERVAL_MS
    );
    return () => clearInterval(timer);
  }, [paused]);

  return (
    // NOT a live region: the tip rotates on a timer and is decorative, so a
    // polite live region would re-announce marketing copy to screen readers
    // repeatedly. The truthful status announcement lives on the loader heading;
    // a SR user can still read this tip on demand. Pause is pointer-only — the
    // tip is decorative and not a focus target, so no keyboard handler.
    <div
      className={css.coachTip}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <BulbOutlined className={css.coachIcon} aria-hidden="true" />
      {/* key re-mounts the line so the fade-in replays on each rotation. */}
      <p key={index} className={css.coachTipText}>
        {WAITING_TIPS[index]}
      </p>
    </div>
  );
};

/**
 * A CSS-only schematic of the layout change: a greyed two-column sheet whose
 * sidebar dissolves, leaving one emerald column.
 *
 * Deliberately ABSTRACT. It illustrates what screening software does to
 * columns in general — it must never imply the user's own upload had a sidebar,
 * because plenty of them are already single-column and a false claim about
 * their file would cost exactly the trust this modal exists to earn.
 *
 * Grey is what the software drops; emerald is what it reads. That is the whole
 * argument, in two colours and one gesture.
 */
const ColumnMorph: React.FC = () => (
  // Hidden wholesale from assistive tech: the evidence list states the same
  // claim in words, and a lone "Single column" announcement out of context
  // informs nobody.
  <figure className={css.morph} aria-hidden="true">
    <div className={css.morphSheet}>
      {/* Row counts are chosen so the sheet reads as a filled page: a sheet
          with a third of its height blank looks like a rendering bug. */}
      <div className={css.morphSide}>
        <span className={css.morphRow} />
        <span className={css.morphRow} />
        <span className={css.morphRow} />
        <span className={css.morphRow} />
      </div>
      <div className={css.morphMain}>
        <span className={css.morphHead} />
        <span className={css.morphRow} />
        <span className={css.morphRow} />
        <span className={css.morphRow} />
        <span className={css.morphRow} />
        <span className={css.morphRow} />
        <span className={css.morphRow} />
      </div>
    </div>
    <figcaption className={css.morphCaption}>
      {/* Stacked in one grid cell so the labels crossfade in place rather than
          shifting the caption's width mid-animation. */}
      <span className={css.morphLabelFrom}>Multi-column</span>
      <span className={css.morphLabelTo}>Single column</span>
    </figcaption>
  </figure>
);

/** Which surface is showing the explainer. Only the closing line and the
 *  dismiss label differ — the argument is identical. */
export type RebuildVariant = 'editor' | 'preview';

// The editor has fix cards beside the preview; the read-only drawer has none
// and no Edit button in its footer, so it must not promise a review queue that
// isn't one click away.
const CLOSING_LINE: Record<RebuildVariant, string> = {
  editor:
    'Every change is listed beside the preview — accept, edit, or skip each one.',
  preview: 'Nothing is locked in — you can adjust any of it before you apply.',
};

const DISMISS_LABEL: Record<RebuildVariant, string> = {
  editor: 'Show me the draft',
  preview: 'Show me the resume',
};

export const REBUILD_EXPLAINER_TITLE = 'Why your resume looks different';

/** The modal title, styled here so both host surfaces get identical type
 *  without importing this module's stylesheet. */
export const RebuildExplainerTitle: React.FC = () => (
  <span className={css.explainerTitle}>{REBUILD_EXPLAINER_TITLE}</span>
);

export interface RebuildExplainerProps {
  variant: RebuildVariant;
  onDismiss: () => void;
}

/**
 * FIRST-RUN ONLY: the answer to "what did you do to my resume?", shown the
 * moment someone first meets a rebuilt draft — in the editor or in the
 * read-only tailored drawer, whichever comes first.
 *
 * It runs BEFORE the editor's "click any section" coachmark by design. The two
 * answer different questions and there is a right order: people have to accept
 * the premise before they'll care about the mechanics, and two hints competing
 * for the same glance means neither is read.
 *
 * Honesty constraint: we cannot claim nothing was removed. Tailoring rewrites
 * wording and sections can be dropped, so the lede owns that up front rather
 * than letting the user discover it and conclude we hid it.
 */
export const RebuildExplainer: React.FC<RebuildExplainerProps> = ({
  variant,
  onDismiss,
}) => (
  <div className={css.explainer}>
    <p className={css.explainerLede}>
      <strong className={css.explainerLedeLead}>You wrote this resume.</strong>{' '}
      We changed how it’s arranged — one column, plain headings, no graphics —
      and tuned some wording for this role.
      <br />
      The layout decides how far your experience travels, with the software and
      the person on the other end.
    </p>

    <ColumnMorph />

    <ul className={css.explainerList}>
      {TEMPLATE_EVIDENCE.map((item) => (
        <li key={item.point} className={css.explainerItem}>
          <strong className={css.explainerPoint}>{item.point}</strong>
          <span className={css.explainerShort}>{item.short}</span>
          <span className={css.explainerSource}>{item.source}</span>
        </li>
      ))}
    </ul>

    <p className={css.explainerClosing}>{CLOSING_LINE[variant]}</p>

    <div className={css.explainerFooter}>
      <a
        className={css.evidenceLink}
        href={PLAYBOOK_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        See the full playbook →
      </a>
      <button
        type="button"
        className={css.explainerDismiss}
        onClick={onDismiss}
      >
        {DISMISS_LABEL[variant]}
      </button>
    </div>
  </div>
);

/**
 * The sourced "why this layout" explanation for the editor's on-demand modal.
 * Presentational — the page owns when it shows.
 */
export const TemplateEvidence: React.FC = () => (
  <div className={css.evidence}>
    <p className={css.evidenceLede}>
      You uploaded your own resume, so why did we rebuild it on a new template?
      Because the layout itself decides how far your experience travels —
      with both the software and the person on the other end.
    </p>
    <ul className={css.evidenceList}>
      {TEMPLATE_EVIDENCE.map((item) => (
        <li key={item.point} className={css.evidenceItem}>
          <strong className={css.evidencePoint}>{item.point}</strong>
          <span className={css.evidenceDetail}>{item.detail}</span>
          <span className={css.evidenceSource}>{item.source}</span>
        </li>
      ))}
    </ul>
    <a
      className={css.evidenceLink}
      href={PLAYBOOK_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      See the full playbook →
    </a>
  </div>
);
